import { useEffect, useState, useMemo } from 'react';
import {
    Box,
    UnorderedList,
    ListItem,
    Flex,
    useDisclosure,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { setAlertStatusBg } from '@/utils/setAlertStatusBg';
import {
    Button,
    Text,
    Alert,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ConfirmationDialog,
} from 'components/v4';
import TransferringTables from 'components/tables/TransferringTables/TransferringTables';
import useAcknowledgePlatformNumbers from '../../queries/useAcknowledgePlatformNumbers';
import useTransferOutPlatformNumbers from '../../queries/useTransferOutPlatformNumbers';
import { TRANSFER_OUT } from '../../utils/transferDirection';
import TransferDirectionOptions from './TransferDirectionOptions';

// Footer buttons share one width, so Cancel/Back and Continue/Save do not
// shift when the modal changes step
const BUTTON_WIDTH = '120px';

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

/**
 * Points the given palettes' CSS variables at red, so everything inside the
 * picker - move buttons, badges, checkboxes, row striping - turns red on a
 * transfer OUT without the shared table components knowing (3069).
 */
const redPaletteSx = (palettes, darkMode) => ({
    ...Object.fromEntries(
        [...new Set(palettes)]
            .filter((name) => name && name !== 'red')
            .flatMap((name) =>
                SHADES.map((shade) => [
                    `--chakra-colors-${name}-${shade}`,
                    `var(--chakra-colors-red-${shade})`,
                ]),
            ),
    ),
    // The empty-table notice is an info alert, so give it the error tint
    '[aria-label="info-alert"]': {
        bg: setAlertStatusBg('error', darkMode) ?? 'red.100',
    },
});

const ConfirmNumbersModal = ({
    isOpen = false,
    onClose = () => {},
    onConfirm = () => {},
    initialNumbers = [],
    refetch,
}) => {
    const safeInitialNumbers = useMemo(
        () => (Array.isArray(initialNumbers) ? initialNumbers : []),
        [initialNumbers],
    );
    const { currentScope } = useSelector((state) => ({ ...state.login }));
    const { darkMode, colorScheme } = useSelector((state) => state.settings);
    const { currentWholesaler: wholesalerID, wholesalers } = useSelector(
        (state) => state.navigationLists,
    );

    const wholesalerName = useMemo(() => {
        return (
            wholesalers?.find(({ id }) => id === wholesalerID)?.name ||
            'Your Telecom Provider'
        );
    }, [wholesalers, wholesalerID]);

    const [confirmationRequired, setConfirmationRequired] =
        useState(safeInitialNumbers);
    const [confirmed, setConfirmed] = useState([]);
    // The modal first asks IN or OUT, then shows the picker for it (3065)
    const [selectedDirection, setSelectedDirection] = useState(null);
    const [direction, setDirection] = useState(null);
    const isTransferOut = direction === TRANSFER_OUT;

    // Confirmation dialog state
    const {
        isOpen: isConfirmDialogOpen,
        onOpen: onConfirmDialogOpen,
        onClose: onConfirmDialogClose,
    } = useDisclosure();

    useEffect(() => {
        if (isOpen && safeInitialNumbers.length > 0) {
            setConfirmationRequired(safeInitialNumbers);
            setConfirmed([]);
        }
    }, [isOpen, safeInitialNumbers]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedDirection(null);
            setDirection(null);
        }
    }, [isOpen]);

    const { mutate: acknowledgeNumbers, isLoading: isAcknowledging } =
        useAcknowledgePlatformNumbers(confirmed, currentScope, refetch);
    const { mutate: transferOutNumbers, isLoading: isTransferringOut } =
        useTransferOutPlatformNumbers(confirmed, currentScope, refetch);
    // Locks the modal while the save runs, so it only closes once the
    // request has come back with a success or an error
    const isSaving = isAcknowledging || isTransferringOut;

    const numberColDefs = [
        {
            isCheckbox: true,
            label: 'Select',
            dataKey: 'checkbox',
        },
        {
            label: 'Number',
            dataKey: 'telephoneNumber',
            minWidth: 200,
            flexGrow: 1,
            maxWidth: 300,
            cellRenderer: ({ rowData }) => {
                const number = rowData?.telephoneNumber;
                return number ? `+${number}` : '';
            },
        },
    ];

    const confirmationRequiredConfig = {
        captionName: 'Confirmation Required ',
        placeholder: 'Search numbers...',
        colDefs: numberColDefs,
        searchField: 'telephoneNumber',
    };

    const confirmedConfig = {
        captionName: 'Confirmed ',
        placeholder: 'Search numbers...',
        colDefs: numberColDefs,
        searchField: 'telephoneNumber',
    };

    const walkthroughIds = {
        moveToChosen: '/confirm-transfer/button/move/confirmed',
        moveToAvailable: '/confirm-transfer/button/move/required',
        moveAllToChosen: '/confirm-transfer/button/move/confirmed/all',
        moveAllToAvailable: '/confirm-transfer/button/move/required/all',
    };

    const handleSaveClick = () => {
        if (confirmed.length > 0) {
            onConfirmDialogOpen();
        }
    };

    const handleConfirmSave = () => {
        const onSettled = () => onConfirm?.(confirmed);
        // OUT marks the numbers as ported out instead of taking ownership (3066)
        if (isTransferOut) {
            transferOutNumbers(undefined, { onSettled });
        } else {
            acknowledgeNumbers(undefined, {
                onError: (error) => toast.error(error?.message),
                onSettled,
            });
        }
    };

    const handleCancel = () => {
        setConfirmationRequired(safeInitialNumbers);
        setConfirmed([]);
        onClose?.();
    };

    // Returns to the IN / OUT choice with the previous choice still selected
    const handleBack = () => {
        setConfirmationRequired(safeInitialNumbers);
        setConfirmed([]);
        setDirection(null);
    };

    if (!isOpen) return null;

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={isSaving ? () => {} : onClose}
                closeOnEsc={!isSaving}
                size="6xl"
                closeOnOverlayClick={!isSaving}>
                <ModalOverlay />
                {/* One size for both steps, so Continue does not resize the
                    modal; the body scrolls on short screens */}
                <ModalContent
                    sx={{
                        marginTop: '30px',
                        marginBottom: '30px',
                        height: 'calc(100vh - 60px)',
                        maxHeight: '900px',
                    }}>
                    <ModalHeader fontSize="xl" fontWeight="bold" pb={2}>
                        {!direction
                            ? 'Confirm Transfer Request'
                            : isTransferOut
                              ? 'Confirm Outbound Transfer'
                              : 'Confirm Inbound Transfer'}
                    </ModalHeader>

                    <ModalBody overflowY="auto">
                        {!direction ? (
                            <TransferDirectionOptions
                                value={selectedDirection}
                                onChange={setSelectedDirection}
                            />
                        ) : (
                            <>
                                {isTransferOut ? (
                                    <Alert
                                        status="error"
                                        mb={3}
                                        py={2}
                                        borderRadius="md">
                                        <Box>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="semibold"
                                                mb={1}>
                                                The following numbers are being
                                                requested to be transferred OUT
                                                from your Operator.
                                            </Text>
                                            <UnorderedList
                                                spacing={0}
                                                ml={4}
                                                fontSize="sm">
                                                <ListItem>
                                                    It is recommended this is
                                                    not completed until after
                                                    confirmation that the new
                                                    Operator has executed
                                                    transfer from their side
                                                </ListItem>
                                                <ListItem>
                                                    Completing this transfer
                                                    shall remove users and
                                                    trunks from the numbers here
                                                </ListItem>
                                                <ListItem>
                                                    If the transfer has already
                                                    been initiated by the new
                                                    Operator, there shall be no
                                                    impact to calling services
                                                </ListItem>
                                            </UnorderedList>
                                        </Box>
                                    </Alert>
                                ) : (
                                    <Alert
                                        status="info"
                                        mb={3}
                                        py={2}
                                        borderRadius="md">
                                        <Box>
                                            <Text
                                                fontSize="sm"
                                                fontWeight="semibold"
                                                mb={1}>
                                                The following numbers have been
                                                detected as existing in the
                                                Teams Tenant and require
                                                confirmation to transfer.
                                            </Text>
                                            <UnorderedList
                                                spacing={0}
                                                ml={4}
                                                fontSize="sm">
                                                <ListItem>
                                                    All numbers shall be
                                                    uploaded to Operator Connect
                                                    under the{' '}
                                                    <Text
                                                        as="span"
                                                        fontWeight="bold"
                                                        color="blue.500">
                                                        {wholesalerName}
                                                    </Text>{' '}
                                                    profile.
                                                </ListItem>
                                                <ListItem>
                                                    All numbers assigned to
                                                    users shall remain assigned.
                                                </ListItem>
                                                <ListItem>
                                                    All calls from these numbers
                                                    shall be routed to{' '}
                                                    <Text
                                                        as="span"
                                                        fontWeight="bold">
                                                        {wholesalerName}
                                                    </Text>
                                                </ListItem>
                                                <ListItem>
                                                    If a number was originally
                                                    assigned to a Direct Routing
                                                    user, to ensure outbound
                                                    calls work, Voice Routing
                                                    Policy must be removed from
                                                    the user. To do this, set
                                                    the user to{' '}
                                                    <b>Managed by TCAP</b> and
                                                    run sync <b>OR</b> have
                                                    customer update user
                                                    manually in Teams Admin
                                                </ListItem>
                                            </UnorderedList>
                                        </Box>
                                    </Alert>
                                )}
                                <Box
                                    sx={
                                        isTransferOut
                                            ? redPaletteSx(
                                                  [
                                                      'brand',
                                                      'blue',
                                                      'messenger',
                                                      colorScheme,
                                                  ],
                                                  darkMode,
                                              )
                                            : undefined
                                    }>
                                    <TransferringTables
                                        availableData={confirmationRequired}
                                        chosenData={confirmed}
                                        availableTableConfig={
                                            confirmationRequiredConfig
                                        }
                                        chosenTableConfig={confirmedConfig}
                                        isLoading={false}
                                        isDisabled={isSaving}
                                        onAvailableDataChange={
                                            setConfirmationRequired
                                        }
                                        onChosenDataChange={setConfirmed}
                                        walkthroughIds={walkthroughIds}
                                        tableHeight={320}
                                    />
                                </Box>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Flex gap={4}>
                            <Button
                                variant="outline"
                                minWidth={BUTTON_WIDTH}
                                onClick={direction ? handleBack : handleCancel}
                                isDisabled={isSaving}
                                data-walkthroughid={
                                    direction
                                        ? '/confirm-transfer/button/back'
                                        : '/confirm-transfer/button/cancel'
                                }>
                                {direction ? 'Back' : 'Cancel'}
                            </Button>
                            {!direction ? (
                                <Button
                                    onClick={() =>
                                        setDirection(selectedDirection)
                                    }
                                    isDisabled={!selectedDirection}
                                    minWidth={BUTTON_WIDTH}
                                    data-walkthroughid="/confirm-transfer/button/continue">
                                    Continue
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSaveClick}
                                    isLoading={isSaving}
                                    isDisabled={confirmed.length < 1}
                                    minWidth={BUTTON_WIDTH}
                                    data-walkthroughid="/confirm-transfer/button/save">
                                    Save
                                </Button>
                            )}
                        </Flex>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <ConfirmationDialog
                isOpen={isConfirmDialogOpen}
                onClose={onConfirmDialogClose}
                onConfirm={handleConfirmSave}
                title={
                    isTransferOut
                        ? 'Confirm Outbound Transfer'
                        : 'Confirm Inbound Transfer'
                }
                message={
                    isTransferOut
                        ? `You are about to transfer out ${confirmed.length} number(s). This removes their users and trunks. Do you want to proceed?`
                        : `You are about to confirm the transfer of ${confirmed.length} number(s). Do you want to proceed?`
                }
                confirmText="Confirm"
                cancelText="Cancel"
                confirmColorScheme={isTransferOut ? 'red' : undefined}
                isLoading={isSaving}
            />
        </>
    );
};

export default ConfirmNumbersModal;
